import { useMemo, useState } from "react";
import { useMatchMedia } from "@jetblue/kit/hooks/use-match-media";
import type { ReactNode } from "react";

export type ComparisonItem = {
  id: string;
  title: ReactNode;
  /**
   * Accessible name to use wherever `title` needs to be exposed as text (e.g.
   * the column picker trigger and its options). Required when `title` isn't
   * a plain string, since in that case there's no reliable way to derive an
   * accessible name from it.
   */
  label?: string;
  /**
  Extra content rendered in the column header, below the title/picker (e.g. marketing copy, a CTA link).
  */
  headerContent?: ReactNode;
  /**
   * Cell content for this item, keyed by section id then row id. Each key
   * must match a `Schema["sections"][number]["id"]` / `Section["rows"][number]["id"]`
   * pair; cells with no matching entry render a "Not available" placeholder.
   */
  sections: Record<string, Record<string, ReactNode>>;
};
/**
 * A single visible comparison column. `columnKey` identifies the column
 * slot itself (its position), not the item currently shown in it — the same
 * item can be selected into more than one column, so `item.id` alone can't
 * be used to key rendered columns. Always key elements on `columnKey`.
 */
export type VisibleColumn = {
  columnKey: number;
  item: ComparisonItem;
};
export const useComparisonColumns = (
  items: ComparisonItem[],
  desktopNumber: number,
  mobileNumber: number,
) => {
  const isMobile = useMatchMedia("(max-width: 47.9375rem)");
  const columnCount = isMobile ? mobileNumber : desktopNumber;
  const isShowColumnPickers = items.length > columnCount;

  const [selectedItemIndexByColumn, setSelectedItemIndexByColumn] = useState<
    (number | undefined)[]
  >([]);

  const visibleCount = Math.min(columnCount, items.length);

  const visibleColumns = useMemo(
    (): VisibleColumn[] =>
      Array.from({ length: visibleCount }, (_, columnKey) => ({
        columnKey,
        item:
          items[selectedItemIndexByColumn[columnKey] ?? columnKey] ??
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- `columnKey` is always < items.length since visibleCount = Math.min(columnCount, items.length).
          items[columnKey]!,
      })),
    [items, selectedItemIndexByColumn, visibleCount],
  );

  const onColumnChange = (columnKey: number, itemId: string) => {
    const itemIndex = items.findIndex((item) => item.id === itemId);

    if (itemIndex === -1) {
      return;
    }

    setSelectedItemIndexByColumn((previous) => {
      const next = [...previous];

      next[columnKey] = itemIndex;

      return next;
    });
  };

  return {
    isMobile,
    onColumnChange,
    showColumnPickers: isShowColumnPickers,
    visibleColumns,
  };
};
